import React, { useEffect, useState } from "react";

import * as api from "../api";
import Switch from "./Switch";

export type Section =
  | "support"
  | "users"
  | "orgs"
  | "conversations"
  | "reconciliation"
  | "audit"
  | "cost";

const SECTION_META: Record<Section, [string, string]> = {
  support: ["Support", 'Diagnose “can’t dial”, kill-switch containment and platform health'],
  users: ["Users", "Search any user across every org · cross-tenant management"],
  orgs: ["Orgs", "Plans, seats, trials, dialing state and prepaid call credits"],
  conversations: ["Calls", "Cross-org call lookup — operational metadata only"],
  reconciliation: ["Reconciliation", "Telephony ledger reconciliation health"],
  audit: ["Audit log", "Every privileged action, attributed and reasoned"],
  cost: ["Cost", "Modeled variable cost, unit economics and rate card — aggregate metadata only"],
};

const NAV_GROUPS: { group: string; items: { key: Section; label: string }[] }[] = [
  { group: "Diagnostics", items: [{ key: "support", label: "Support" }] },
  {
    group: "Directory",
    items: [
      { key: "users", label: "Users" },
      { key: "orgs", label: "Orgs" },
      { key: "conversations", label: "Calls" },
    ],
  },
  {
    group: "Observability",
    items: [
      { key: "reconciliation", label: "Reconciliation" },
      { key: "audit", label: "Audit log" },
    ],
  },
  {
    group: "Business",
    items: [{ key: "cost", label: "Cost" }],
  },
];

function NavIcon({ k }: { k: Section }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    style: { flex: "none" as const },
  };
  switch (k) {
    case "support":
      return <svg {...common}><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>;
    case "users":
      return (
        <svg {...common}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case "orgs":
      return (
        <svg {...common}>
          <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
          <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
          <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
          <path d="M10 6h4M10 10h4M10 14h4M10 18h4" />
        </svg>
      );
    case "conversations":
      return (
        <svg {...common}>
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
      );
    case "reconciliation":
      return (
        <svg {...common}>
          <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
          <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
          <path d="M7 21h10M12 3v18M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2" />
        </svg>
      );
    case "audit":
      return (
        <svg {...common}>
          <path d="M8 21h12a2 2 0 0 0 2-2v-2H10v2a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v3h4" />
          <path d="M19 17V5a2 2 0 0 0-2-2H9" />
          <path d="M13 8h3M13 11h3" />
        </svg>
      );
    case "cost":
      return (
        <svg {...common}>
          <path d="M3 3v18h18" />
          <path d="m19 9-5 5-4-4-3 3" />
        </svg>
      );
  }
}

function navStyle(active: boolean): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    textAlign: "left",
    padding: "10px 12px",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
    fontSize: 14,
    fontWeight: active ? 700 : 500,
    color: active ? "#fff" : "rgba(240,238,245,0.6)",
    background: active ? "rgba(255,255,255,0.1)" : "transparent",
    boxShadow: active ? "inset 3px 0 0 var(--violet-400)" : "none",
    transition: "background 120ms ease, color 120ms ease",
  };
}

const groupLabelStyle: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "rgba(240,238,245,0.4)",
  padding: "14px 12px 6px",
};

function HealthBanner() {
  const [health, setHealth] = useState<api.AnalyticsHealth | null>(null);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    let alive = true;
    api.getAnalyticsHealth().then((h) => alive && setHealth(h), () => {});
    return () => {
      alive = false;
    };
  }, []);
  if (!health || !health.stale || dismissed) return null;
  const detail = !health.forwarding_configured
    ? "forwarding is not configured"
    : `no successful forward in >24h (${health.consecutive_failures} consecutive failures)`;
  return (
    <div
      role="alert"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "11px 32px",
        background: "var(--danger-500)",
        color: "#fff",
        flex: "none",
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "none" }}>
        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4M12 17h.01" />
      </svg>
      <span style={{ fontSize: 13.5, lineHeight: 1.45, flex: 1 }}>
        Analytics forwarding is STALE in <strong>{health.environment}</strong> — events are being
        recorded but not reaching Mixpanel ({detail}). {health.events_recorded_last_hour} event(s)
        recorded in the last hour.
      </span>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        style={{
          background: "rgba(255,255,255,0.16)",
          border: "none",
          color: "#fff",
          width: 26,
          height: 26,
          borderRadius: 7,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "none",
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
      </button>
    </div>
  );
}

export default function Shell({
  section,
  onNavigate,
  operatorName,
  operatorInitials,
  operatorRoleLabel,
  isSuperadmin,
  viewAsStaff,
  onToggleViewAs,
  onSignOut,
  toast,
  onDismissToast,
  children,
}: {
  section: Section;
  onNavigate: (s: Section) => void;
  operatorName: string;
  operatorInitials: string;
  operatorRoleLabel: string;
  isSuperadmin: boolean;
  viewAsStaff: boolean;
  onToggleViewAs: (on: boolean) => void;
  onSignOut: () => void;
  toast: string | null;
  onDismissToast: () => void;
  children: React.ReactNode;
}) {
  const [title, subtitle] = SECTION_META[section];
  const envLabel = api.getEnvLabel();
  return (
    <div style={{ display: "flex", height: "100vh", width: "100%", overflow: "hidden", background: "var(--paper)" }}>
      <aside
        className="ws-dark"
        style={{
          width: 246,
          flex: "none",
          display: "flex",
          flexDirection: "column",
          background: "var(--gradient-hud)",
          borderRight: "1px solid var(--border-on-dark)",
          color: "var(--text-on-dark)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "22px 20px 18px" }}>
          <img src="/mark-white.png" alt="Wingscript" style={{ height: 26, width: "auto", display: "block", opacity: 0.98 }} />
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
            <span style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 18, letterSpacing: "-0.02em", color: "#fff" }}>Wingscript</span>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase", color: "var(--sky-300)", marginTop: 4 }}>ops console</span>
          </div>
        </div>

        <nav style={{ display: "flex", flexDirection: "column", gap: 3, padding: "8px 12px", flex: 1, overflow: "auto" }}>
          {NAV_GROUPS.map((g) => (
            <React.Fragment key={g.group}>
              <div style={groupLabelStyle}>{g.group}</div>
              {g.items.map((it) => (
                <button key={it.key} type="button" onClick={() => onNavigate(it.key)} style={navStyle(section === it.key)}>
                  <NavIcon k={it.key} />
                  <span>{it.label}</span>
                </button>
              ))}
            </React.Fragment>
          ))}
        </nav>

        <div style={{ padding: 14, borderTop: "1px solid var(--border-on-dark)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "6px 4px 12px" }}>
            <div style={{ width: 36, height: 36, flex: "none", borderRadius: 10, background: "var(--gradient-lift)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 14, color: "#fff" }}>{operatorInitials}</div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{operatorName}</div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--sky-300)", letterSpacing: "0.06em" }}>{operatorRoleLabel}</div>
            </div>
          </div>
          {isSuperadmin && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "9px 11px", border: "1px solid var(--border-on-dark)", borderRadius: 10, background: "rgba(255,255,255,0.03)", marginBottom: 8 }}>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(240,238,245,0.62)" }}>View as staff</span>
              <Switch checked={viewAsStaff} onChange={onToggleViewAs} ariaLabel="View as staff" />
            </div>
          )}
          <button type="button" onClick={onSignOut} style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", padding: "9px 11px", border: "none", borderRadius: 10, background: "transparent", color: "rgba(240,238,245,0.62)", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "none" }}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" /></svg>
            Sign out
          </button>
        </div>
      </aside>

      <main style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, background: "var(--paper)" }}>
        <header style={{ display: "flex", alignItems: "center", gap: 20, padding: "20px 32px", borderBottom: "1px solid var(--border-hairline)", flex: "none" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ margin: 0, fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 22, letterSpacing: "-0.02em", color: "var(--ink-900)" }}>{title}</h1>
            <p style={{ margin: "3px 0 0", fontSize: 13.5, color: "var(--text-muted)" }}>{subtitle}</p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flex: "none" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ink-500)", border: "1px solid var(--border-default)", borderRadius: 999, padding: "5px 12px" }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--sky-400)", boxShadow: "0 0 0 3px rgba(90,180,255,0.2)", animation: "ws-live 1.8s ease-in-out infinite" }} />
              {envLabel}
            </span>
          </div>
        </header>

        {section !== "support" && <HealthBanner />}

        <div style={{ flex: 1, overflow: "auto" }}>
          {toast && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 32px 0", padding: "11px 16px", background: "var(--success-100)", border: "1px solid #BFE6CC", color: "var(--success-500)", borderRadius: 10, fontSize: 13.5, fontWeight: 500 }}>
              <span style={{ flex: 1 }}>{toast}</span>
              <button type="button" onClick={onDismissToast} aria-label="Dismiss" style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer", opacity: 0.6, display: "inline-flex", padding: 2 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}
